import { FC, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import CriteriaField from '@components/EntityDrawers/components/CiaInputsGroup/CriteriaField';
import { isShownByDependency } from '@components/EntityDrawers/components/CiaInputsGroup/utils/helpers';
import { ISelectOption } from '@constants/entities/ui';
import { alpha, Box, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { CIASettingsCriteriaType } from '@store/services/questionnaire/types';
import { themePalette } from '@theme/muiTheme';
import {
  CriteriaKey,
  getCriteriaPath,
  resetDependentCriteria,
} from '@views/ProjectSettings/components/ProjectDetailsSettings/helpers/criteriaBlock.helpers';

const revealPulse = keyframes({
  '0%': {
    borderColor: themePalette.primary.main,
    boxShadow: `0 0 0 4px ${alpha(themePalette.primary.main, 0.4)}`,
  },
  '100%': {
    boxShadow: `0 0 0 0 ${alpha(themePalette.primary.main, 0)}`,
  },
});

type Props = {
  title: string;
  fields: CIASettingsCriteriaType[];
  criteriaKey: CriteriaKey;
  onChange?: (criteriaId: string, values: ISelectOption[]) => void;
  revealOnMenuClose?: boolean;
};

const CriteriaBlock: FC<Props> = ({
  title,
  fields,
  criteriaKey,
  onChange,
  revealOnMenuClose = false,
}) => {
  const { getValues, setValue } = useFormContext();
  const [revealedIds, setRevealedIds] = useState<string[]>([]);
  const [pulsingIds, setPulsingIds] = useState<string[]>([]);
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const isShown = (field: CIASettingsCriteriaType) =>
    isShownByDependency(field.dependency, criteriaKey, getValues);

  useEffect(() => {
    const lastPulsingId = pulsingIds[pulsingIds.length - 1];

    if (lastPulsingId) {
      fieldRefs.current[lastPulsingId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [pulsingIds]);

  const handleCriteriaChange = (id: string) => (values: ISelectOption[]) => {
    onChange?.(id, values);

    resetDependentCriteria(fields, id, values, criteriaKey, setValue);

    if (!values.length) {
      setValue(getCriteriaPath(criteriaKey, id), [], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (revealOnMenuClose) {
      setRevealedIds((prev) =>
        prev.filter((revealedId) => {
          const revealedField = fields.find((field) => field.id === revealedId);

          return !!revealedField && isShown(revealedField);
        }),
      );
    }
  };

  const handleMenuClose = () => {
    const newlyShownIds = fields
      .filter(
        (field) =>
          field.dependency && !revealedIds.includes(field.id) && isShown(field),
      )
      .map((field) => field.id);

    if (!newlyShownIds.length) return;

    setRevealedIds((prev) => [...prev, ...newlyShownIds]);
    setPulsingIds(newlyShownIds);
  };

  return (
    <Stack gap={4}>
      {!!title && <Typography variant="h4">{title}</Typography>}

      {fields.map((field) => {
        if (!isShown(field)) return null;

        if (!revealOnMenuClose) {
          return (
            <CriteriaField
              key={field.id}
              field={field}
              nameKey={getCriteriaPath(criteriaKey, field.id)}
              changeHandler={handleCriteriaChange(field.id)}
            />
          );
        }

        if (field.dependency && !revealedIds.includes(field.id)) return null;

        return (
          <Box
            key={field.id}
            ref={(node: HTMLDivElement | null) => {
              fieldRefs.current[field.id] = node;
            }}
            sx={
              pulsingIds.includes(field.id)
                ? {
                    '& .MuiOutlinedInput-notchedOutline': {
                      animation: `${revealPulse} 1s ease-out`,
                    },
                  }
                : undefined
            }
          >
            <CriteriaField
              field={field}
              nameKey={getCriteriaPath(criteriaKey, field.id)}
              changeHandler={handleCriteriaChange(field.id)}
              onClose={handleMenuClose}
            />
          </Box>
        );
      })}
    </Stack>
  );
};

export default CriteriaBlock;
